import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import type {
  Bootstrap,
  Milestone,
  PrepItem,
  Task,
  TaskInput,
  Team,
  TeamInput,
  Volunteer,
  VolunteerInput,
} from '@nepal-football/shared';

import { apiFetch } from './client';

// ─── Query keys ────────────────────────────────────────────────────────────
export const QK = {
  bootstrap:   ['bootstrap'] as const,
  tasks:       ['tasks'] as const,
  teams:       ['teams'] as const,
  prep:        ['prep'] as const,
  milestones:  ['milestones'] as const,
  volunteers:  ['volunteers'] as const,
};

// ─── Auth helpers ──────────────────────────────────────────────────────────

const TOKEN_KEY = 'admin_token';

/**
 * Read the admin JWT from sessionStorage. Mirrors the source-of-truth in
 * `hooks/AdminAuthContext`. Used by mutation hooks below to attach the
 * Authorization header without requiring React context inside the API layer.
 */
function getAdminToken(): string | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function authHeaders(): Record<string, string> {
  const token = getAdminToken();
  return token ? { authorization: `Bearer ${token}` } : {};
}

// ─── Queries ───────────────────────────────────────────────────────────────
export function useBootstrap() {
  return useQuery({
    queryKey: QK.bootstrap,
    queryFn: () => apiFetch<Bootstrap>('/bootstrap'),
  });
}

export function useTasks() {
  return useQuery({
    queryKey: QK.tasks,
    queryFn: () => apiFetch<Task[]>('/tasks'),
  });
}

export function useTeams() {
  return useQuery({
    queryKey: QK.teams,
    queryFn: () => apiFetch<Team[]>('/teams'),
  });
}

export function usePrepItems() {
  return useQuery({
    queryKey: QK.prep,
    queryFn: () => apiFetch<PrepItem[]>('/prep-items'),
  });
}

export function useMilestones() {
  return useQuery({
    queryKey: QK.milestones,
    queryFn: () => apiFetch<Milestone[]>('/milestones'),
  });
}

// ─── Mutations: optimistic update + rollback on error ──────────────────────

interface TogglePayload {
  id: string;
  done: boolean;
}

/**
 * Build a generic toggle mutation that:
 *   1. Cancels in-flight queries for the affected list(s)
 *   2. Snapshots previous data
 *   3. Optimistically updates the cache (and bootstrap cache if present)
 *   4. Rolls back + toasts on error
 *   5. Invalidates the queries on settle
 */
function makeToggleMutation<T extends { id: string; done: boolean }>(opts: {
  endpoint: (id: string) => string;
  listKey: readonly string[];
  bootstrapKey: keyof Bootstrap;
}) {
  return function useToggle(): UseMutationResult<T, Error, TogglePayload, ToggleSnapshot<T>> {
    const qc = useQueryClient();
    return useMutation<T, Error, TogglePayload, ToggleSnapshot<T>>({
      mutationFn: ({ id, done }) =>
        apiFetch<T>(opts.endpoint(id), {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify({ done }),
        }),

      onMutate: async ({ id, done }) => {
        await qc.cancelQueries({ queryKey: opts.listKey });
        await qc.cancelQueries({ queryKey: QK.bootstrap });

        const prevList = qc.getQueryData<T[]>(opts.listKey);
        const prevBootstrap = qc.getQueryData<Bootstrap>(QK.bootstrap);

        if (prevList) {
          qc.setQueryData<T[]>(
            opts.listKey,
            prevList.map((it) => (it.id === id ? { ...it, done } : it)),
          );
        }
        if (prevBootstrap) {
          const list = prevBootstrap[opts.bootstrapKey] as unknown as T[];
          qc.setQueryData<Bootstrap>(QK.bootstrap, {
            ...prevBootstrap,
            [opts.bootstrapKey]: list.map((it) => (it.id === id ? { ...it, done } : it)),
          });
        }

        return { prevList, prevBootstrap };
      },

      onError: (_err, _vars, ctx) => {
        if (ctx?.prevList) qc.setQueryData(opts.listKey, ctx.prevList);
        if (ctx?.prevBootstrap) qc.setQueryData(QK.bootstrap, ctx.prevBootstrap);
        toast.error("Couldn't save change, please retry");
      },

      onSettled: () => {
        qc.invalidateQueries({ queryKey: opts.listKey });
        qc.invalidateQueries({ queryKey: QK.bootstrap });
      },
    });
  };
}

interface ToggleSnapshot<T> {
  prevList: T[] | undefined;
  prevBootstrap: Bootstrap | undefined;
}

export const useToggleTask = makeToggleMutation<Task>({
  endpoint: (id) => `/tasks/${id}`,
  listKey: QK.tasks,
  bootstrapKey: 'tasks',
});

export const useTogglePrepItem = makeToggleMutation<PrepItem>({
  endpoint: (id) => `/prep-items/${id}`,
  listKey: QK.prep,
  bootstrapKey: 'prepItems',
});

export const useToggleMilestone = makeToggleMutation<Milestone>({
  endpoint: (id) => `/milestones/${id}`,
  listKey: QK.milestones,
  bootstrapKey: 'milestones',
});

// ─── Volunteer queries & admin mutations ───────────────────────────────────

export function useVolunteers() {
  return useQuery({
    queryKey: QK.volunteers,
    queryFn: () => apiFetch<Volunteer[]>('/volunteers'),
  });
}

export function useCreateVolunteer(): UseMutationResult<Volunteer, Error, VolunteerInput> {
  const qc = useQueryClient();
  return useMutation<Volunteer, Error, VolunteerInput>({
    mutationFn: (input) =>
      apiFetch<Volunteer>('/volunteers', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.volunteers });
    },
  });
}

export function useUpdateVolunteer(): UseMutationResult<
  Volunteer,
  Error,
  { id: string } & VolunteerInput
> {
  const qc = useQueryClient();
  return useMutation<Volunteer, Error, { id: string } & VolunteerInput>({
    mutationFn: ({ id, ...input }) =>
      apiFetch<Volunteer>(`/volunteers/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.volunteers });
    },
  });
}

export function useDeleteVolunteer(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/volunteers/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.volunteers });
    },
    onError: () => {
      toast.error("Couldn't delete volunteer, please retry");
    },
  });
}


// ─── Team admin mutations ──────────────────────────────────────────────────

export function useCreateTeam(): UseMutationResult<Team, Error, TeamInput> {
  const qc = useQueryClient();
  return useMutation<Team, Error, TeamInput>({
    mutationFn: (input) =>
      apiFetch<Team>('/teams', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.teams });
      qc.invalidateQueries({ queryKey: QK.bootstrap });
    },
  });
}

export function useUpdateTeam(): UseMutationResult<
  Team,
  Error,
  { id: string } & TeamInput
> {
  const qc = useQueryClient();
  return useMutation<Team, Error, { id: string } & TeamInput>({
    mutationFn: ({ id, ...input }) =>
      apiFetch<Team>(`/teams/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.teams });
      qc.invalidateQueries({ queryKey: QK.bootstrap });
    },
  });
}

export function useDeleteTeam(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/teams/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.teams });
      qc.invalidateQueries({ queryKey: QK.bootstrap });
    },
    onError: () => {
      toast.error("Couldn't delete team, please retry");
    },
  });
}

// ─── Task admin mutations ──────────────────────────────────────────────────

export function useCreateTask(): UseMutationResult<Task, Error, TaskInput> {
  const qc = useQueryClient();
  return useMutation<Task, Error, TaskInput>({
    mutationFn: (input) =>
      apiFetch<Task>('/tasks', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tasks });
      qc.invalidateQueries({ queryKey: QK.bootstrap });
    },
  });
}

export function useUpdateTask(): UseMutationResult<
  Task,
  Error,
  { id: string } & TaskInput
> {
  const qc = useQueryClient();
  return useMutation<Task, Error, { id: string } & TaskInput>({
    mutationFn: ({ id, ...input }) =>
      apiFetch<Task>(`/tasks/${id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tasks });
      qc.invalidateQueries({ queryKey: QK.bootstrap });
    },
  });
}

export function useDeleteTask(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) =>
      apiFetch<void>(`/tasks/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tasks });
      qc.invalidateQueries({ queryKey: QK.bootstrap });
    },
    onError: () => {
      toast.error("Couldn't delete task, please retry");
    },
  });
}
